import { channelKey } from '@/lib/api/scenario-runs.api';

import { SCENARIO_CHANNELS } from '@repo/shared-types';

import type { ScenarioChannel, ScenarioRunCase } from '@/lib/api/scenario-runs.api';

export interface MatrixRow {
  scenarioId: string | null;
  scenarioName: string;
  /** One entry per column, in column order; null when the pair was not run. */
  cells: Array<ScenarioRunCase | null>;
}

export interface ScenarioMatrix {
  columns: ScenarioChannel[];
  rows: MatrixRow[];
}

const rowKey = (c: ScenarioRunCase): string => c.scenario_id ?? `name:${c.scenario_name}`;

/**
 * Rows = distinct scenarios ordered by name; columns = distinct channels in the
 * platform's `SCENARIO_CHANNELS` order (unknown pairs are appended at the end).
 * A cell is the case for that scenario × channel, or null.
 */
export const buildMatrix = (cases: ScenarioRunCase[]): ScenarioMatrix => {
  const present = new Set(cases.map((c) => channelKey(c)));
  const columns: ScenarioChannel[] = SCENARIO_CHANNELS.filter((ch) => present.has(channelKey(ch)));
  const known = new Set(columns.map((ch) => channelKey(ch)));
  for (const c of cases) {
    const key = channelKey(c);
    if (!known.has(key)) {
      known.add(key);
      columns.push({ transport: c.transport, modality: c.modality });
    }
  }

  const byScenario = new Map<string, { scenarioId: string | null; scenarioName: string }>();
  for (const c of cases) {
    const key = rowKey(c);
    if (!byScenario.has(key)) {
      byScenario.set(key, { scenarioId: c.scenario_id, scenarioName: c.scenario_name });
    }
  }

  const rows: MatrixRow[] = [...byScenario.entries()]
    .sort(([, a], [, b]) => a.scenarioName.localeCompare(b.scenarioName))
    .map(([key, meta]) => ({
      ...meta,
      cells: columns.map(
        (ch) => cases.find((c) => rowKey(c) === key && channelKey(c) === channelKey(ch)) ?? null,
      ),
    }));

  return { columns, rows };
};
